import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, Award, Heart, Target, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';

// Images
const aboutImages = {
  brandGraphic: 'https://customer-assets.emergentagent.com/job_a036c687-b73f-4aaa-a278-ca6139d7a148/artifacts/4xom3ry9_AMS_Master_Brand_Graphic.png',
  founders: 'https://customer-assets.emergentagent.com/job_a036c687-b73f-4aaa-a278-ca6139d7a148/artifacts/yru5suau_IMG_1696.JPG',
};

export default function AboutPage() {
  return (
    <div className="page-transition" data-testid="about-page">
      <SEOHead 
        page="about"
        title="About AMS | Artisan Market & Studio"
        description="Learn about Artisan Market & Studio - a complete ecosystem helping artisans turn their passion into profit through events, education, and software."
      />

      {/* Hero with Brand Graphic */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-primary/5 to-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {/* Brand Graphic */}
            <div className="mb-8">
              <img 
                src={aboutImages.brandGraphic} 
                alt="Artisan Market & Studio - One Vision, Three Powerful Branches: AMS Events, AMS University, AMS Compass" 
                className="w-full max-w-3xl mx-auto rounded-2xl shadow-lg"
              />
            </div>
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-foreground mt-6 mb-6">
              Building a Platform Where Artisans Thrive
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              AMS is more than a marketplace—it's a complete ecosystem that helps artisans build profitable, sustainable businesses doing what they love.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-accent font-accent text-2xl">How It All Began</span>
              <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-6 mt-2">
                Our Story
              </h2>
              <div className="space-y-6 text-lg text-muted-foreground">
                <p>
                  Hi! We are Harry Layman and Kathy Copeland and in 2014, we set out to sell our artwork at craft show venues. Like many of you, we had found so much joy in making our artwork but quickly we had to find places to put it! Friends told us - this is really great, you should sell it! And so, one day, we just started.
                </p>
                <p>
                  We quickly learned there's A LOT more to it than just showing up with a bunch of tables and a tent! From algorithms to marketing, websites to social media, we very quickly got overwhelmed and realized that we needed help! As our network grew, we learned how to navigate this industry which quite often takes advantage of gaps in skills, gaps in experience, and the generational gap.
                </p>
                <p>
                  Now, we've found out where we can help our artisan community the most. By bringing us together to share our knowledge, improve our tools, and create opportunities that matter to everyone, regardless of their time in this industry.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={aboutImages.founders}
                  alt="Harry Layman and Kathy Copeland - Founders of AMS"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl p-4 shadow-lg border hidden lg:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Award className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Since 2014</p>
                    <p className="text-sm text-muted-foreground">Empowering Artisans</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                We believe every artisan deserves the tools, knowledge, and opportunities to turn their creative passion into a thriving business. Too many talented makers struggle with the business side—pricing, marketing, finding the right events, and building consistent income.
              </p>
              <p className="text-lg text-muted-foreground mb-6">
                That's why we created AMS: a comprehensive platform that combines curated selling events, business education, and powerful software tools to help artisans succeed.
              </p>
              <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-xl">
                <Target className="w-10 h-10 text-primary flex-shrink-0" />
                <p className="text-foreground font-medium">
                  "A platform that helps artisans build profitable businesses through events, education, and software."
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-6"
            >
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-border text-center">
                <div className="text-4xl font-bold text-primary mb-2">100+</div>
                <div className="text-muted-foreground">Artisan Members</div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-border text-center">
                <div className="text-4xl font-bold text-primary mb-2">50+</div>
                <div className="text-muted-foreground">Events Hosted</div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-border text-center">
                <div className="text-4xl font-bold text-primary mb-2">10+</div>
                <div className="text-muted-foreground">Years of Experience</div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-border text-center">
                <div className="flex justify-center mb-2">
                  <Award className="w-10 h-10 text-amber-500" />
                </div>
                <div className="text-muted-foreground">Best of Loudoun 2024</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-semibold">
              What We Stand For
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Users,
                title: 'Community First',
                description: 'We believe artisans succeed together. Our community shares knowledge, supports each other, and celebrates wins collectively.',
              },
              {
                icon: Target,
                title: 'Data-Driven Growth',
                description: 'Stop guessing about pricing and events. We provide the tools and insights to make smart business decisions.',
              },
              {
                icon: Heart,
                title: 'Passion Meets Profit',
                description: 'Creating beautiful things shouldn\'t mean struggling financially. We help you build a real business, not just a hobby.',
              },
            ].map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-muted/30 p-8 rounded-2xl text-center"
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-serif font-semibold mb-3">{value.title}</h3>
                <p className="text-muted-foreground">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-6">
              Ready to Grow Your Artisan Business?
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              Join over 100 artisans who are building profitable businesses with AMS. Start your free 7-day trial of AMS Compass today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://www.madeartisanmarkets.com" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-white text-primary hover:bg-white/90 rounded-full px-8" data-testid="about-trial-cta">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Start Free 7-Day Trial
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="rounded-full px-8 border-white text-white hover:bg-white/10" data-testid="about-contact-cta">
                  Contact Us
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
